import React, { useState, useEffect } from "react";
import { useTranslation } from "shared/contexts/I18nContext";
import { useKeycloak } from "@react-keycloak/web";
import AvatarMenu from "./AvatarMenu";
import { ProtectedVisibility } from "shared/security/ProtectedRoute";
import { getUserRoleProfileId } from "shared/utils/SessionDataUtils";

const Header = ({
  isDropdownOpen,
  toggleDropdown,
  toggleTheme,
  isDarkMode,
  currentPage,
  navigateTo,
  toggleMockMode,
  useMockData,
  profilePictureUrl,
}) => {
  const { t, setLocale } = useTranslation();
  const { keycloak } = useKeycloak();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [userName, setUserName] = useState("");
  const [userRole, setUserRole] = useState("");

  const userId = getUserRoleProfileId();


  useEffect(() => {
    if (keycloak?.authenticated && keycloak.idTokenParsed) {
      const { given_name, family_name, role } = keycloak.idTokenParsed;
      const format = (s) =>
        s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";
      const first = format(given_name);
      const last = format(family_name);
      const name = first + " " + last;
      setUserName(`${name}`);

      // Retrieve and parse back to object
      const userProfile = JSON.parse(
        sessionStorage.getItem("keycloak_user_profile")
      );

      // Now you can access its attributes
      if (userProfile) {
        const { zoneinfo } = userProfile;
        setUserRole(zoneinfo ?? "");
      }
    }
  }, [keycloak]);

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-white shadow-md md:px-10 dark:bg-gray-800 relative z-50">
      {/* Logo */}
      <div className="flex items-center">
        <img src="logo.png" alt="SahajGyan" className="h-15" />
      </div>

      {/* Desktop Nav */}
      <div className="hidden sm:flex flex-wrap justify-center sm:justify-start space-x-4 sm:space-x-6 md:space-x-8">
        <ProtectedVisibility requiredRoles={['ROLE_TEACHER']}>

          <a
            href="#"
            onClick={() => navigateTo("teacherDashboard")}
            className="py-2 px-2 sm:px-0 font-medium text-gray-600 border-b-2 border-transparent transition-colors hover:text-green-700 hover:border-green-700 dark:text-gray-300 dark:hover:text-green-400 dark:hover:border-green-400"
          >
            {t("Dashboard")}
          </a>
        </ProtectedVisibility>

        <a
          href="#"
          onClick={() => navigateTo("resources")}
          className="py-2 px-2 sm:px-0 font-medium text-gray-600 border-b-2 border-transparent transition-colors hover:text-green-700 hover:border-green-700 dark:text-gray-300 dark:hover:text-green-400 dark:hover:border-green-400"
        >
          {t("resources")}
        </a>

        <ProtectedVisibility requiredRoles={['ROLE_STUDENT']}>

          <a
            href="#"
            onClick={() => navigateTo("studentHub", userId)}
            className="py-2 px-2 sm:px-0 font-medium text-gray-600 border-b-2 border-transparent transition-colors hover:text-green-700 hover:border-green-700 dark:text-gray-300 dark:hover:text-green-400 dark:hover:border-green-400"
          >
            Student Hub
          </a>
        </ProtectedVisibility>



        <ProtectedVisibility requiredRoles={['ROLE_TEACHER', 'ROLE_PARENT']}>
          <a
            href="#"
            onClick={() => navigateTo("students")}
            className={`py-2 px-2 sm:px-0 font-medium border-b-2 transition-colors ${[
              "students",
              "studentRoster",
              "createStudent",
              "updateStudent",
            ].includes(currentPage)
              ? "text-green-700 border-green-700 dark:text-green-400 dark:border-green-400"
              : "text-gray-600 border-transparent hover:text-green-700 hover:border-green-700 dark:text-gray-300 dark:hover:text-green-400 dark:hover:border-green-400"
              }`}
          >
            {t("students")}
          </a>
        </ProtectedVisibility>

        <ProtectedVisibility requiredRoles={['ROLE_TEACHER']}>
          <a
            href="#"
            onClick={() => navigateTo("courses")}
            className={`py-2 px-2 sm:px-0 font-medium border-b-2 transition-colors ${[
              "courses",
              "myBatches",
              "createCourse",
              "updateCourse",
              "createBatch",
              "updateBatch",
            ].includes(currentPage)
              ? "text-green-700 border-green-700 dark:text-green-400 dark:border-green-400"
              : "text-gray-600 border-transparent hover:text-green-700 hover:border-green-700 dark:text-gray-300 dark:hover:text-green-400 dark:hover:border-green-400"
              }`}
          >
            {t("courses")}
          </a>
          <ProtectedVisibility requiredRoles={['ROLE_TEACHER']} beta={true}>
            <a
              href="#"
              onClick={() => navigateTo("concepts")}
              className={`py-2 px-2 sm:px-0 font-medium border-b-2 transition-colors ${currentPage === "concepts"
                ? "text-green-700 border-green-700 dark:text-green-400 dark:border-green-400"
                : "text-gray-600 border-transparent hover:text-green-700 hover:border-green-700 dark:text-gray-300 dark:hover:text-green-400 dark:hover:border-green-400"
                }`}
            >
              <div className="relative inline-block">
                <span> {t("Concepts")}</span>
                <span
                  className="absolute -top-2 -right-6  font-bold
               px-2 py-0.5 text-xs font-small tracking-wide
               text-gray-800 bg-gray-100 rounded-full 
               dark:bg-gray-700 dark:text-gray-300
               transform rotate-12 shadow-sm"
                >
                  beta
                </span>
              </div>

            </a>
          </ProtectedVisibility>
          <a
            href="#"
            onClick={() => navigateTo("evaluations")}
            className={`py-2 px-2 sm:px-0 font-medium border-b-2 transition-colors ${currentPage === "evaluations"
              ? "text-green-700 border-green-700 dark:text-green-400 dark:border-green-400"
              : "text-gray-600 border-transparent hover:text-green-700 hover:border-green-700 dark:text-gray-300 dark:hover:text-green-400 dark:hover:border-green-400"
              }`}
          >
            {t("evaluations")}
          </a>
        </ProtectedVisibility>
      </div>

      {/* Mobile Hamburger */}
      <button
        className="sm:hidden text-xl text-gray-700 dark:text-white ml-auto"
        onClick={() => setIsDrawerOpen(true)}
      >
        ☰
      </button>

      {/* AvatarMenu */}
      <div className="hidden sm:block ml-4">
        <AvatarMenu
          isDropdownOpen={isDropdownOpen}
          toggleDropdown={toggleDropdown}
          toggleTheme={toggleTheme}
          isDarkMode={isDarkMode}
          toggleMockMode={toggleMockMode}
          useMockData={useMockData}
          userName={userName}
          userRole={userRole}
          profilePictureUrl={profilePictureUrl}
          setLocale={setLocale}
          navigateTo={navigateTo}
        />
      </div>

      {/* Mobile Drawer Navigation */}
      {isDrawerOpen && (
        <aside className="fixed inset-0 z-50 sm:hidden">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setIsDrawerOpen(false)}
          />
          <div className="absolute top-0 right-0 w-64 h-full bg-white dark:bg-gray-800 p-4 shadow-xl overflow-y-auto">
            <button
              className="text-xl mb-4 text-right w-full text-gray-700 dark:text-gray-200"
              onClick={() => setIsDrawerOpen(false)}
            >
              ✖
            </button>
            <div className="flex justify-end mb-6">
              {/* User Info with AvatarMenu */}
              <div className="flex items-center space-x-3 mb-6">
                <AvatarMenu
                  isDropdownOpen={isDropdownOpen}
                  toggleDropdown={toggleDropdown}
                  toggleTheme={toggleTheme}
                  isDarkMode={isDarkMode}
                  toggleMockMode={toggleMockMode}
                  useMockData={useMockData}
                  userName={userName}
                  userRole={userRole}
                  profilePictureUrl={profilePictureUrl}
                  setLocale={setLocale}
                  navigateTo={navigateTo}
                />
              </div>
            </div>
            {/* Mobile Nav Links */}
            <nav className="flex flex-col space-y-4">
              {["home", "resources", "students", "courses", "evaluations"].map(
                (item) => (
                  <a
                    key={item}
                    href="#"
                    onClick={() => {
                      navigateTo(item);
                      setIsDrawerOpen(false);
                    }}
                    className={`text-base font-medium px-3 py-2 rounded ${currentPage === item
                      ? "text-green-700 dark:text-green-400"
                      : "text-gray-600 hover:text-green-700 dark:text-gray-300 dark:hover:text-green-400"
                      }`}
                  >
                    {t(item)}
                  </a>
                )
              )}
            </nav>
          </div>
        </aside>
      )}
    </div>
  );
};

export default Header;
